import { apiClient } from '../client';

/**
 * Client for `/api/comms/shout-outs` (Stream E / ADR-027 §3.2). Mirrors
 * `server/src/routes/comms/shoutOuts.routes.ts` +
 * `modules/comms/shout-outs/*` — peer recognition tagged to the CURRENT
 * V/TO core values. Reads are org-transparent; the summary feeds the
 * People Analyzer "Recognition (90d)" strip.
 */

export interface ShoutOut {
  id: string;
  organizationId: string;
  coreValue: string;
  message: string;
  giverMembershipId: string | null;
  giverNameSnapshot: string;
  recipientMembershipId: string | null;
  recipientNameSnapshot: string;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ShoutOutPage {
  items: ShoutOut[];
  hasMore: boolean;
}

// ── List ────────────────────────────────────────────────────────────────

export interface ListShoutOutsParams {
  recipientMembershipId?: string;
  limit?: number;
  /** Cursor: rows strictly older than this `createdAt` ISO time. */
  before?: string;
}

export function buildShoutOutListParams(params: ListShoutOutsParams): URLSearchParams {
  const sp = new URLSearchParams();
  if (params.recipientMembershipId) sp.set('recipientMembershipId', params.recipientMembershipId);
  if (params.limit !== undefined) sp.set('limit', String(params.limit));
  if (params.before) sp.set('before', params.before);
  return sp;
}

export async function listShoutOuts(params: ListShoutOutsParams = {}): Promise<ShoutOutPage> {
  const qs = buildShoutOutListParams(params).toString();
  const res = await apiClient.get<{ data: ShoutOutPage }>(`/api/comms/shout-outs${qs ? `?${qs}` : ''}`);
  return res.data.data;
}

// ── Create / archive ────────────────────────────────────────────────────

export interface CreateShoutOutInput {
  recipientMembershipId: string;
  coreValue: string;
  message: string;
}

/** The 400-error codes the create endpoint can return (all caller errors). */
export type CreateShoutOutErrorCode =
  | 'cannot_shout_out_self'
  | 'recipient_not_found'
  | 'no_core_values_defined'
  | 'core_value_not_recognized';

export async function createShoutOut(input: CreateShoutOutInput): Promise<ShoutOut> {
  const res = await apiClient.post<{ data: ShoutOut }>('/api/comms/shout-outs', input);
  return res.data.data;
}

export async function deleteShoutOut(id: string): Promise<void> {
  await apiClient.delete(`/api/comms/shout-outs/${encodeURIComponent(id)}`);
}

// ── Summary (People Analyzer "Recognition" strip) ───────────────────────

export interface ShoutOutValueCount {
  coreValue: string;
  count: number;
}

export interface RecentShoutOut {
  id: string;
  coreValue: string;
  message: string;
  giverNameSnapshot: string;
  createdAt: string;
}

export interface MemberShoutOutSummary {
  membershipId: string;
  byValue: ShoutOutValueCount[];
  total: number;
  recent: RecentShoutOut[];
}

export interface ShoutOutSummaryParams {
  /** ≤50 recipient membership ids — serialized as a CSV. */
  membershipIds: string[];
  /** Look-back window in days (1..365, default 90 server-side). */
  sinceDays?: number;
}

export function buildShoutOutSummaryParams(params: ShoutOutSummaryParams): URLSearchParams {
  const sp = new URLSearchParams();
  sp.set('membershipIds', params.membershipIds.join(','));
  if (params.sinceDays !== undefined) sp.set('sinceDays', String(params.sinceDays));
  return sp;
}

export async function summarizeShoutOuts(params: ShoutOutSummaryParams): Promise<MemberShoutOutSummary[]> {
  const qs = buildShoutOutSummaryParams(params).toString();
  const res = await apiClient.get<{ data: { members: MemberShoutOutSummary[] } }>(
    `/api/comms/shout-outs/summary?${qs}`,
  );
  return res.data.data.members;
}
